/* import __COLOCATED_TEMPLATE__ from './banner-static.hbs'; */
/**
## Static Banner

This component should be used for static, non-interactive messaging. These are
simply informational and do not require merchant action to resolve. For more
interactive messaging, please use the [flash service](/glass/docs/development/flash.html) and [`banner-flash`](/components/glass/banner-flash.html).

```example
@hbs
<BannerStatic @type="INFO">
  Some info here
</BannerStatic>
<BannerStatic @type="SUCCESS">
  You win!
</BannerStatic>
<BannerStatic @type="WARNING">
  You win a warning!
</BannerStatic>
<BannerStatic @type="ERROR">
  Something is statically wrong
</BannerStatic>
```

## Multi-line Static Banner

The component always yields to a block of text, but the default body styling
does not properly style multi-line messages. Two utility CSS classes are provided
for this use case: `banner-static__body--multiline-title` and `banner-static__body--multiline-body`.

```example
@hbs
<BannerStatic @type="INFO">
  <p class="banner-static__body--multiline-title">
    A title for your banner here
  </p>
  <p class="banner-static__body--multiline-body">
    More details about your banner explaining why it is being shown and what the merchant should do instead.
  </p>
</BannerStatic>
```

### Parameters
@param {String} [type=null] The type of a flash message can be `ERROR`, `INFO`, `SUCCESS` or `WARNING`
@param {Action} [dismiss=null] Optional dismiss action. If provided, a close icon will be rendered that simply calls this action on click, which is then responsible for updating whatever state is needed to dismiss the banner.
 */

import Component from '@ember/component';
import { computed, set } from '@ember/object';
import alertTriangleIconComponent from '@square/glass-ui/components/svg/icon-alert-triangle';
import checkmarkCircleIconComponent from '@square/glass-ui/components/svg/icon-checkmark-circle';
import infoCircleIconComponent from '@square/glass-ui/components/svg/icon-info-circle';
const TYPE = {
  ERROR: 'ERROR',
  INFO: 'INFO',
  SUCCESS: 'SUCCESS',
  WARNING: 'WARNING'
};

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  classNames: ['banner-static'],
  classNameBindings: ['typeClass'],
  type: null,
  dismiss: null,
  typeClass: computed('type', function () {
    switch (this.get('type')) {
      case TYPE.ERROR:
        return 'banner-static--is-error';
      case TYPE.INFO:
        return 'banner-static--is-info';
      case TYPE.SUCCESS:
        return 'banner-static--is-successful';
      case TYPE.WARNING:
        return 'banner-static--is-warning';
      default:
        return '';
    }
  }),
  iconComponent: computed('_iconComponent', 'type', {
    get() {
      if (this._iconComponent !== undefined) {
        return this._iconComponent;
      }
      switch (this.get('type')) {
        case TYPE.INFO:
          return infoCircleIconComponent;
        case TYPE.ERROR:
          return alertTriangleIconComponent;
        case TYPE.SUCCESS:
          return checkmarkCircleIconComponent;
        case TYPE.WARNING:
          return alertTriangleIconComponent;
        default:
          return infoCircleIconComponent;
      }
    },
    set(_, value) {
      return set(this, '_iconComponent', value);
    }
  }),
  iconColor: computed('type', function () {
    switch (this.get('type')) {
      case TYPE.ERROR:
        return 'red';
      case TYPE.INFO:
        return 'blue-medium';
      case TYPE.SUCCESS:
        return 'green';
      case TYPE.WARNING:
        return 'base';
      default:
        return 'base';
    }
  })
});